import React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import {
    BriefcaseSearch20Regular,
    CalendarLtr20Regular,
    Dismiss20Regular,
    FilterDismiss20Regular,
    People20Regular,
} from "@fluentui/react-icons";
// SP2 (Mail-Filterleiste): eigenes Design in der warmen Chat-Palette —
// bewusst KEINE Fluent-Komponenten (kein <Input>/<Button>/<Badge>); nur
// Fluent-ICONS sind erlaubt (Review-Vorgabe A7).
import { ACTIVE_BG, AUB, INK, INK2, LINE, PLUM } from "../chat/chatTheme";
import {
    EMPTY_MAIL_FILTER,
    isMailFilterActive,
    type MailDateRange,
    type MailFilterState,
} from "./mailFilter";

const useStyles = makeStyles({
    /* Leiste an der bisherigen Suchposition (über der Liste). */
    root: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "8px",
        ...shorthands.padding("6px", "12px"),
        backgroundColor: "#ffffff",
        ...shorthands.borderBottom("1px", "solid", LINE),
        flexShrink: 0,
    },
    /* Absender/Empfänger — gestyltes natives <input> mit Personen-Icon. */
    textField: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        boxSizing: "border-box",
        height: "30px",
        flexGrow: 1,
        flexBasis: "160px",
        minWidth: 0,
        ...shorthands.padding("0", "8px"),
        ...shorthands.border("1px", "solid", LINE),
        ...shorthands.borderRadius("8px"),
        backgroundColor: "#ffffff",
        transitionProperty: "border-color, box-shadow",
        transitionDuration: "0.15s",
        ":focus-within": {
            ...shorthands.borderColor(AUB),
            boxShadow: `0 0 0 1px ${AUB}`,
        },
    },
    textFieldLarge: {
        height: "38px",
    },
    fieldIcon: {
        color: INK2,
        flexShrink: 0,
    },
    input: {
        flexGrow: 1,
        minWidth: 0,
        ...shorthands.border("0px", "none"),
        outlineStyle: "none",
        backgroundColor: "transparent",
        color: INK,
        fontSize: "13px",
        fontFamily: "inherit",
        ...shorthands.padding("0"),
        "::placeholder": { color: INK2 },
    },
    clearBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "20px",
        height: "20px",
        ...shorthands.padding("0"),
        ...shorthands.border("0px", "none"),
        ...shorthands.borderRadius("6px"),
        backgroundColor: "transparent",
        color: INK2,
        cursor: "pointer",
        ":hover": { backgroundColor: LINE, color: INK },
    },
    /* Zeitraum-Chips — eigene Toggle-Buttons (kein Fluent). */
    rangeGroup: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        flexShrink: 0,
    },
    rangeIcon: {
        color: INK2,
        flexShrink: 0,
        marginRight: "2px",
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        height: "26px",
        ...shorthands.padding("0", "10px"),
        ...shorthands.border("1px", "solid", LINE),
        ...shorthands.borderRadius("13px"),
        backgroundColor: "#ffffff",
        color: INK,
        fontSize: "12px",
        fontWeight: 500,
        fontFamily: "inherit",
        whiteSpace: "nowrap" as any,
        cursor: "pointer",
        transitionProperty: "background-color, border-color, color",
        transitionDuration: "0.12s",
        ":hover": {
            backgroundColor: ACTIVE_BG,
            ...shorthands.borderColor(ACTIVE_BG),
        },
    },
    chipLarge: {
        height: "32px",
        ...shorthands.padding("0", "12px"),
    },
    chipActive: {
        backgroundColor: PLUM,
        ...shorthands.borderColor(PLUM),
        color: "#ffffff",
        ":hover": {
            backgroundColor: PLUM,
            ...shorthands.borderColor(PLUM),
            color: "#ffffff",
        },
    },
    /* Rechter Block: „Filter aktiv"-Badge + Zurücksetzen. */
    tail: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginLeft: "auto",
        flexShrink: 0,
    },
    activeBadge: {
        display: "inline-flex",
        alignItems: "center",
        ...shorthands.padding("2px", "8px"),
        ...shorthands.borderRadius("10px"),
        backgroundColor: ACTIVE_BG,
        color: AUB,
        fontSize: "11px",
        fontWeight: 600,
        whiteSpace: "nowrap" as any,
    },
    resetBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        height: "26px",
        ...shorthands.padding("0", "8px"),
        ...shorthands.border("0px", "none"),
        ...shorthands.borderRadius("6px"),
        backgroundColor: "transparent",
        color: INK2,
        fontSize: "12px",
        fontWeight: 500,
        fontFamily: "inherit",
        whiteSpace: "nowrap" as any,
        cursor: "pointer",
        ":hover": { backgroundColor: LINE, color: INK },
    },
});

const RANGE_LABELS: { key: Exclude<MailDateRange, null>; label: string }[] = [
    { key: "today", label: "Heute" },
    { key: "yesterday", label: "Gestern" },
    { key: "last7", label: "Letzte 7 Tage" },
];

export type MailFilterBarProps = {
    value: MailFilterState;
    onChange: (next: MailFilterState) => void;
    /** "large" = höhere Touch-Variante (Phone). */
    size?: "medium" | "large";
};

/**
 * SP2 — Filterleiste an der bisherigen Suchposition (über der Liste): freier
 * Absender-/Empfänger-Text + Zeitraum-Chips (Heute/Gestern/Letzte 7 Tage).
 * Wirkt clientseitig auf Liste bzw. Suchtreffer; „Filter aktiv"-Badge +
 * Zurücksetzen erscheinen, sobald ein Filter greift.
 */
export const MailFilterBar: React.FC<MailFilterBarProps> = ({ value, onChange, size = "medium" }) => {
    const styles = useStyles();
    const large = size === "large";
    const active = isMailFilterActive(value);

    const toggleRange = (key: Exclude<MailDateRange, null>) =>
        onChange({ ...value, range: value.range === key ? null : key });
    const toggleVorgaenge = () => onChange({ ...value, nurVorgaenge: !value.nurVorgaenge });

    return (
        <div className={styles.root} role="group" aria-label="E-Mails filtern">
            <div className={mergeClasses(styles.textField, large && styles.textFieldLarge)}>
                <People20Regular className={styles.fieldIcon} />
                <input
                    className={styles.input}
                    type="text"
                    value={value.text}
                    placeholder="Absender/Empfänger filtern …"
                    aria-label="Nach Absender oder Empfänger filtern"
                    autoComplete="off"
                    onChange={(e) => onChange({ ...value, text: e.target.value })}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") onChange({ ...value, text: "" });
                    }}
                />
                {value.text && (
                    <button
                        type="button"
                        className={styles.clearBtn}
                        aria-label="Absender-/Empfänger-Filter löschen"
                        onClick={() => onChange({ ...value, text: "" })}
                    >
                        <Dismiss20Regular />
                    </button>
                )}
            </div>

            <div className={styles.rangeGroup}>
                <CalendarLtr20Regular className={styles.rangeIcon} />
                {RANGE_LABELS.map(({ key, label }) => {
                    const isActive = value.range === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={mergeClasses(
                                styles.chip,
                                large && styles.chipLarge,
                                isActive && styles.chipActive,
                            )}
                            aria-pressed={isActive}
                            onClick={() => toggleRange(key)}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>

            {/* L1 — „Vorgänge": zeigt nur Mails, die zu einem Vorgang gehören.
                Ein eigener Chip neben den Zeitraum-Chips, gleiche Optik. */}
            <div className={styles.rangeGroup}>
                <BriefcaseSearch20Regular className={styles.rangeIcon} />
                <button
                    type="button"
                    className={mergeClasses(
                        styles.chip,
                        large && styles.chipLarge,
                        !!value.nurVorgaenge && styles.chipActive,
                    )}
                    aria-pressed={!!value.nurVorgaenge}
                    title="Nur Mails zeigen, die zu einem Vorgang gehören"
                    onClick={toggleVorgaenge}
                >
                    Vorgänge
                </button>
            </div>

            {active && (
                <div className={styles.tail}>
                    <span className={styles.activeBadge}>Filter aktiv</span>
                    <button
                        type="button"
                        className={styles.resetBtn}
                        aria-label="Filter zurücksetzen"
                        onClick={() => onChange(EMPTY_MAIL_FILTER)}
                    >
                        <FilterDismiss20Regular />
                        Zurücksetzen
                    </button>
                </div>
            )}
        </div>
    );
};
